import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { toast } from "sonner";
import ImageUpload from "../components/ImageUpload";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import BranchesSection from "../components/BranchesSection";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Download, Plus, Power, Share2, Trash2 } from "lucide-react";

const DAYS = [
  ["sat", "السبت"],
  ["sun", "الأحد"],
  ["mon", "الإثنين"],
  ["tue", "الثلاثاء"],
  ["wed", "الأربعاء"],
  ["thu", "الخميس"],
  ["fri", "الجمعة"],
];

export default function Settings() {
  const { user } = useAuth();
  const branchContext = useBranch();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user?.role === "EMPLOYEE" && !branchContext?.loaded) return;
    if (user?.role === "EMPLOYEE" && !branchContext?.currentId) return;
    api.get("/restaurants/mine").then((response) => setR(response.data));
  }, [branchContext?.currentId, branchContext?.loaded, user?.role]);

  const isOwner = user?.role === "OWNER";
  const hasNoAssignedBranch = (
    !isOwner
    && branchContext?.loaded
    && !branchContext.currentId
  );

  if (hasNoAssignedBranch) {
    return (
      <DashboardShell title="الإعدادات">
        <div className="py-10 text-center text-gray-500" data-testid="settings-no-assigned-branch">
          لم يتم تعيينك لأي فرع — تواصل مع المالك
        </div>
      </DashboardShell>
    );
  }

  if (!r) return <DashboardShell title="الإعدادات"><div className="text-gray-500">جاري التحميل…</div></DashboardShell>;

  const canEditHours = isOwner || user?.permissions?.hours_edit;
  const canToggleAvailability = isOwner || user?.permissions?.availability_toggle;
  const canManageQr = isOwner || user?.permissions?.qr_manage;
  const save = async (patch = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", patch);
      setR(data); toast.success("تم الحفظ");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const branchPath = r.branch_code ? `/${encodeURIComponent(r.branch_code)}` : "";
  const publicUrl = `${window.location.origin}/r/${r.slug}${branchPath}`;
  const posterUrl = `/dashboard/qr-poster?slug=${encodeURIComponent(r.slug)}${
    r.branch_id ? `&branch_id=${encodeURIComponent(r.branch_id)}` : ""
  }`;

  return (
    <DashboardShell title="الإعدادات" restaurantName={r.name}>
      {!isOwner && (
        <section
          className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
          data-testid="employee-settings-branch-section"
        >
          <label className="block" data-testid="employee-settings-branch-label">
            <span className="text-xs font-bold text-gray-600">الفرع</span>
            <select
              value={branchContext?.currentId || ""}
              onChange={(event) => branchContext?.selectBranch(event.target.value)}
              className={
                "mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 font-bold "
                + "text-gray-800 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              }
              data-testid="employee-settings-branch-select"
            >
              {(branchContext?.branches || []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}{branch.active === false ? " (متوقف)" : ""}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {isOwner && <BranchesSection />}

      {isOwner && (
        <section className="bg-white rounded-2xl border border-orange-100 p-4 space-y-3 mb-4">
          <ImageUpload value={r.logo_url && r.logo_url.startsWith("/") ? r.logo_url : (r.logo_url && !r.logo_url.startsWith("http") ? "" : r.logo_url)} onChange={v => save({ logo_url: v || "" })} label="شعار المحل" testid="s-logo-upload" />
          <ImageUpload value={r.cover_url} onChange={v => save({ cover_url: v || "" })} label="غلاف المحل (اختياري)" testid="s-cover-upload" />
          <Input label="اسم المحل" value={r.name || ""} onChange={v => setR(s=>({...s, name: v}))} onBlur={() => save({ name: r.name })} testid="s-name" />
          <Input label="عبارة تعريفية" value={r.tagline || ""} onChange={v => setR(s=>({...s, tagline: v}))} onBlur={() => save({ tagline: r.tagline })} testid="s-tagline" />
        </section>
      )}

      {!isOwner && canToggleAvailability && (
        <section
          className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
          data-testid="employee-availability-section"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-xs text-gray-500">حالة استقبال الطلبات</div>
              <div className="mt-0.5 flex items-center gap-2 text-lg font-extrabold">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    r.availability?.accepting_orders ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                {r.availability?.reason || "جاري التحقق"}
              </div>
              {r.availability?.sub_reason && (
                <div className="mt-0.5 text-xs text-gray-500">{r.availability.sub_reason}</div>
              )}
            </div>
            <button
              onClick={() => save({ orders_accepting_now: !r.orders_accepting_now })}
              disabled={busy}
              data-testid="employee-toggle-orders"
              className={
                "pill inline-flex items-center gap-1 px-4 py-2.5 text-sm font-bold "
                + (r.orders_accepting_now
                  ? "border border-red-100 bg-red-50 text-red-700"
                  : "border border-green-100 bg-green-50 text-green-700")
              }
            >
              <Power size={14}/>
              {r.orders_accepting_now ? "إيقاف الطلبات" : "تشغيل الطلبات"}
            </button>
          </div>
        </section>
      )}

      {!isOwner && canManageQr && (
        <QrManager publicUrl={publicUrl} posterUrl={posterUrl} />
      )}

      {!isOwner && canEditHours && (
        <HoursEditor
          hours={r.hours || {}}
          busy={busy}
          onSave={(hours) => save({ hours })}
        />
      )}
    </DashboardShell>
  );
}

function QrManager({ publicUrl, posterUrl }) {
  const copy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("تم نسخ الرابط");
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "رابط الطلب", url: publicUrl });
      } catch {
        return;
      }
      return;
    }
    await copy();
  };
  const downloadQr = () => {
    const svg = document.getElementById("employee-qr-svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 600;
    const image = new Image();
    image.onload = () => {
      const context = canvas.getContext("2d");
      context.fillStyle = "#fff";
      context.fillRect(0, 0, 600, 600);
      context.drawImage(image, 0, 0, 600, 600);
      const anchor = document.createElement("a");
      anchor.href = canvas.toDataURL("image/png");
      anchor.download = "qr-ordering-link.png";
      anchor.click();
    };
    image.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  };

  return (
    <section
      className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
      data-testid="employee-qr-section"
    >
      <h2 className="mb-2 font-extrabold">رابط ورمز QR للطلبات</h2>
      <div
        className="break-all rounded-2xl bg-gray-50 px-3 py-2 text-sm num-badge"
        data-testid="employee-qr-url"
      >
        {publicUrl}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={copy}
          className={
            "pill inline-flex items-center gap-1 border border-orange-200 bg-white px-3 py-2 "
            + "text-xs font-bold text-orange-700"
          }
          data-testid="employee-copy-link"
        >
          <Copy size={14}/>
          نسخ الرابط
        </button>
        <button
          onClick={share}
          className={
            "pill inline-flex items-center gap-1 border border-orange-200 bg-white px-3 py-2 "
            + "text-xs font-bold text-orange-700"
          }
          data-testid="employee-share-link"
        >
          <Share2 size={14}/>
          مشاركة
        </button>
        <button
          onClick={downloadQr}
          className={
            "pill inline-flex items-center gap-1 border border-orange-200 bg-white px-3 py-2 "
            + "text-xs font-bold text-orange-700"
          }
          data-testid="employee-download-qr"
        >
          <Download size={14}/>
          تحميل QR
        </button>
        <Link
          to={posterUrl}
          className="pill brand-grad px-3 py-2 text-xs font-bold text-white"
          data-testid="employee-print-poster"
        >
          ملصق A4
        </Link>
      </div>
      <div className="mt-4 text-center">
        <div className="inline-block rounded-2xl border border-orange-100 bg-white p-3">
          <QRCodeSVG
            id="employee-qr-svg"
            value={publicUrl}
            size={180}
            level="M"
            bgColor="#ffffff"
            fgColor="#111827"
            data-testid="employee-qr-code"
          />
        </div>
      </div>
    </section>
  );
}

function HoursEditor({ hours: initialHours, busy, onSave }) {
  const [hours, setHours] = useState(initialHours);
  const updateDay = (day, patch) => {
    setHours((current) => ({
      ...current,
      [day]: { ...(current[day] || {}), ...patch },
    }));
  };
  const addShift = (day) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    updateDay(day, {
      enabled: true,
      shifts: [
        ...(current.shifts || []),
        { opening_time: "12:00", closing_time: "22:00" },
      ],
    });
  };
  const updateShift = (day, index, patch) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    const shifts = [...(current.shifts || [])];
    shifts[index] = { ...shifts[index], ...patch };
    updateDay(day, { shifts });
  };
  const removeShift = (day, index) => {
    const current = hours[day] || { enabled: true, shifts: [] };
    updateDay(day, {
      shifts: current.shifts.filter((_, currentIndex) => currentIndex !== index),
    });
  };

  return (
    <section
      className="mb-4 bg-white rounded-2xl border border-orange-100 p-4"
      data-testid="employee-hours-section"
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="font-extrabold">ساعات العمل</div>
        <button
          onClick={() => onSave(hours)}
          disabled={busy}
          className="pill brand-grad px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
          data-testid="employee-save-hours"
        >
          {busy ? "جاري الحفظ…" : "حفظ"}
        </button>
      </div>
      <div className="mb-3 text-xs text-gray-500">
        الإغلاق قبل الفتح يعني أن الفترة تمتد إلى اليوم التالي.
      </div>
      <div className="space-y-3">
        {DAYS.map(([key, name]) => {
          const day = hours[key] || {
            enabled: true,
            shifts: [{ opening_time: "10:00", closing_time: "23:59" }],
          };
          return (
            <div
              key={key}
              className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3"
              data-testid={`employee-hours-${key}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={day.enabled !== false}
                    onChange={(event) => updateDay(key, { enabled: event.target.checked })}
                    className="h-4 w-4 accent-orange-600"
                    data-testid={`employee-hours-enable-${key}`}
                  />
                  <span className="font-bold">{name}</span>
                </label>
                <button
                  onClick={() => addShift(key)}
                  className={
                    "pill inline-flex items-center gap-1 border border-orange-200 bg-white px-3 "
                    + "py-1 text-xs font-bold text-orange-700"
                  }
                  data-testid={`employee-hours-add-shift-${key}`}
                >
                  <Plus size={12}/>
                  فترة
                </button>
              </div>
              {day.enabled !== false && (
                <div className="space-y-2">
                  {(day.shifts || []).map((shift, index) => (
                    <div
                      key={index}
                      className={
                        "grid grid-cols-2 gap-2 rounded-xl border border-orange-100 bg-white p-2"
                      }
                      data-testid={`employee-shift-${key}-${index}`}
                    >
                      <TimeField
                        label="وقت الفتح"
                        value={shift.opening_time ?? shift.open ?? ""}
                        onChange={(value) => updateShift(key, index, { opening_time: value })}
                        testid={`employee-shift-open-${key}-${index}`}
                      />
                      <TimeField
                        label="وقت الإغلاق"
                        value={shift.closing_time ?? shift.close ?? ""}
                        onChange={(value) => updateShift(key, index, { closing_time: value })}
                        testid={`employee-shift-close-${key}-${index}`}
                      />
                      <button
                        onClick={() => removeShift(key, index)}
                        className={
                          "col-span-2 pill inline-flex items-center justify-center gap-1 border "
                          + "border-red-100 bg-red-50 py-1.5 text-xs font-bold text-red-600"
                        }
                        data-testid={`employee-shift-delete-${key}-${index}`}
                      >
                        <Trash2 size={12}/>
                        حذف هذه الفترة
                      </button>
                    </div>
                  ))}
                  {(day.shifts || []).length === 0 && (
                    <div className="text-xs text-gray-500" data-testid={`employee-no-shifts-${key}`}>
                      لا فترات — اليوم مغلق
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Input({ label, value, onChange, onBlur, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} data-testid={testid}
        className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
    </label>
  );
}

function TimeField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testid}
        className={
          "mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 outline-none "
          + "focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
        }
      />
    </label>
  );
}
